import React, { useEffect, useId, useRef, useState } from 'react';
import { MapPin } from 'lucide-react';
import { searchLocations } from '../services/locationSearch';

export function locationFromProfile(profile = {}) {
  if (!profile.area && !profile.city && !profile.country && !profile.locationLabel) return null;
  return {
    area: profile.area || '',
    city: profile.city || '',
    country: profile.country || '',
    lat: typeof profile.lat === 'number' ? profile.lat : null,
    lng: typeof profile.lng === 'number' ? profile.lng : null,
    label: profile.locationLabel || [profile.area, profile.city, profile.country].filter(Boolean).join(', '),
    placeId: profile.locationPlaceId || '',
    locationProvider: profile.locationProvider || '',
  };
}

export default function LocationAutocomplete({ value, onChange, required = false }) {
  const id = useId();
  const containerRef = useRef(null);
  const [text, setText] = useState(value?.label || '');
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [retry, setRetry] = useState(0);

  useEffect(() => { if (value?.label) setText(value.label); }, [value?.label]);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    setResults([]); setActive(-1); setError('');
    if (value || text.trim().length < 3) { setStatus(''); return () => controller.abort(); }
    setStatus('loading');
    const timeout = setTimeout(async () => {
      try {
        const locations = await searchLocations(text, controller.signal);
        if (cancelled) return;
        setResults(locations);
        setStatus(locations.length ? 'ready' : 'empty');
      } catch (err) {
        if (!cancelled && err.name !== 'AbortError') {
          setError(err.message || 'Unable to search locations. Please try again.');
          setStatus('error');
        }
      }
    }, 600);
    return () => { cancelled = true; clearTimeout(timeout); controller.abort(); };
  }, [text, value, retry]);

  const choose = (location) => {
    setText(location.label); setOpen(false); setActive(-1);
    onChange(location);
  };
  const keyDown = (event) => {
    if (event.key === 'Escape') { setOpen(false); return; }
    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && results.length) {
      event.preventDefault(); setOpen(true);
      setActive((index) => event.key === 'ArrowDown' ? (index + 1) % results.length : (index <= 0 ? results.length - 1 : index - 1));
    }
    if (event.key === 'Enter' && open && active >= 0 && results[active]) {
      event.preventDefault(); choose(results[active]);
    } else if (event.key === 'Enter' && !value) {
      event.preventDefault(); setOpen(true);
    }
  };
  const showResults = open && !value && results.length > 0;
  const hasCoordinates = Number.isFinite(value?.lat) && Number.isFinite(value?.lng);
  return <div className="location-field" ref={containerRef} onBlur={(event) => {
    if (!containerRef.current?.contains(event.relatedTarget)) setOpen(false);
  }}>
    <label htmlFor={id}>LOCATION{required ? ' *' : ''}</label>
    <div className="location-input">
      <MapPin size={18} aria-hidden="true" />
      <input id={id} type="text" role="combobox" value={text} autoComplete="off"
        aria-autocomplete="list" aria-expanded={Boolean(showResults)} aria-controls={`${id}-results`}
        aria-activedescendant={showResults && active >= 0 ? `${id}-option-${active}` : undefined}
        aria-describedby={`${id}-status`} aria-required={required}
        placeholder="Search area, city, or country"
        onChange={(event) => { setText(event.target.value); setOpen(true); onChange(null); }}
        onFocus={() => setOpen(true)} onKeyDown={keyDown} />
    </div>
    {showResults && <ul className="location-suggestions" id={`${id}-results`} role="listbox">
      {results.map((location, index) => <li key={`${location.label}:${location.lat}:${location.lng}`}
        id={`${id}-option-${index}`} role="option" aria-selected={active === index}
        className={active === index ? 'active' : ''}>
        <button type="button" tabIndex={-1} onMouseDown={(event) => event.preventDefault()} onClick={() => choose(location)}>{location.label}</button>
      </li>)}
    </ul>}
    <div id={`${id}-status`} aria-live="polite" className="location-status">
      {value ? hasCoordinates ? <small className="sr-only">Location selected.</small> : <small className="location-error">This saved location has no coordinates. Select a location again.</small> : status === 'loading' ? <small>Searching locations...</small> : status === 'empty' ? <small>No locations found.</small> : error ? <small className="location-error">{error} <button type="button" className="text-button" onClick={() => { setOpen(true); setRetry((count) => count + 1); }}>Retry</button></small> : text.trim().length > 0 ? <small>{text.trim().length < 3 ? 'Enter at least 3 characters.' : 'Select a location.'}</small> : null}
    </div>
  </div>;
}
